/**
 * Когда показывать экран «У вас включён VPN» и когда его убирать.
 *
 * Требование владельца 18.09.2026: при входе с VPN сразу закрыть приложение, а как только VPN
 * выключен — сразу открыть. Браузер о выключении VPN не сообщает, поэтому проверка повторяется:
 * - каждые [RECHECK_BLOCKED_MS], пока плашка висит, — человек выключает VPN и ждёт;
 * - сразу при возврате на вкладку, фокусе окна и смене сети — VPN выключают в другом
 *   приложении и возвращаются сюда;
 * - каждые [RECHECK_CLEAR_MS] в остальное время — VPN могли включить посреди работы.
 *
 * Первая проверка держит приложение замороженным не дольше [FIRST_CHECK_TIMEOUT_MS]: если сервер
 * молчит, приложение открывается, а проверка продолжает идти своим чередом. «Не знаем» не
 * закрывает приложение никогда — только ответ сервера «VPN». Кнопки «проверить» нет по решению
 * владельца: плашка уходит сама.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { checkVpn, VPN_BLOCKED_TEXT, type VpnVerdict } from './vpnCheck';

export const FIRST_CHECK_TIMEOUT_MS = 4_000;
export const RECHECK_BLOCKED_MS = 2_000;
export const RECHECK_CLEAR_MS = 30_000;

export type VpnGateStatus = 'checking' | 'clear' | 'blocked';

export interface VpnGateState {
  status: VpnGateStatus;
  /** Текст под заголовком плашки. */
  message: string;
}

export function useVpnGate(check: () => Promise<VpnVerdict | null> = checkVpn): VpnGateState {
  const [status, setStatus] = useState<VpnGateStatus>('checking');
  const [message, setMessage] = useState(VPN_BLOCKED_TEXT);

  const statusRef = useRef<VpnGateStatus>('checking');
  const inFlight = useRef(false);
  const timer = useRef<number | null>(null);
  const alive = useRef(true);
  const checkRef = useRef(check);
  checkRef.current = check;

  const apply = useCallback((next: VpnGateStatus) => {
    statusRef.current = next;
    setStatus(next);
  }, []);

  const schedule = useCallback((run: () => void) => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    const delay = statusRef.current === 'blocked' ? RECHECK_BLOCKED_MS : RECHECK_CLEAR_MS;
    timer.current = window.setTimeout(run, delay);
  }, []);

  const run = useCallback(async () => {
    if (inFlight.current || !alive.current) return;
    inFlight.current = true;
    try {
      const verdict = await checkRef.current();
      if (!alive.current) return;
      if (verdict !== null) {
        if (verdict.vpn) {
          setMessage(verdict.message?.trim() || VPN_BLOCKED_TEXT);
          apply('blocked');
        } else {
          apply('clear');
        }
      } else if (statusRef.current === 'checking') {
        // Сервер не ответил на самой первой проверке — открываем: «не знаем» не повод закрывать.
        apply('clear');
      }
    } finally {
      inFlight.current = false;
      if (alive.current) schedule(() => void run());
    }
  }, [apply, schedule]);

  useEffect(() => {
    alive.current = true;
    void run();
    const giveUp = window.setTimeout(() => {
      if (statusRef.current === 'checking') apply('clear');
    }, FIRST_CHECK_TIMEOUT_MS);

    const now = () => void run();
    const onVisible = () => {
      if (document.visibilityState === 'visible') now();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', now);
    window.addEventListener('online', now);
    const connection = (navigator as Navigator & { connection?: EventTarget }).connection;
    connection?.addEventListener?.('change', now);

    return () => {
      alive.current = false;
      window.clearTimeout(giveUp);
      if (timer.current !== null) window.clearTimeout(timer.current);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', now);
      window.removeEventListener('online', now);
      connection?.removeEventListener?.('change', now);
    };
  }, [run, apply]);

  return { status, message };
}
